import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { isCustomEmailTemplateEnabled } from '../../env';
import { useToastStore } from '../../stores/toastStore';
import { emailsService } from '../../services/emailsService';
import { selectHasCustomTemplate, useCustomEmailTemplateStore } from '../../stores/customEmailTemplateStore';
import { definedLanguages } from '../../types/customEmailTemplate';
import type { TemplateLanguage } from '../../types/customEmailTemplate';
import type { RelanceEmailDto } from '../../types/customerFollowUp/email';
import { toCanonical, toDisplay } from '../../features/relance/customEmailTemplate/customTemplateMarkers';

export type CustomSelection =
    | { status: 'applied'; draft: RelanceEmailDto }
    /** The requested language and template version are already the ones shown: nothing was overwritten. */
    | { status: 'unchanged' }
    /** The company has no custom template. */
    | { status: 'unavailable' };

interface AppliedTemplate {
    language: TemplateLanguage;
    version: number;
}

const inDisplayForm = (draft: RelanceEmailDto, language: TemplateLanguage): RelanceEmailDto => ({
    ...draft,
    subject: toDisplay(draft.subject, language),
    bodyTone: toDisplay(draft.bodyTone, language),
});

/**
 * The "Personnalisé" chip of the email tab. The user edits the custom template with localized markers
 * (`{Nom du contact}`); the server stores and renders the canonical `{{contactName}}` form.
 * `applyDraft` receives the subject and body to show, already in display form.
 */
export const useCustomTemplateDraft = (relanceId: string, applyDraft: (draft: RelanceEmailDto) => void) => {
    const { t } = useTranslation();
    const showToast = useToastStore(s => s.showToast);
    const template = useCustomEmailTemplateStore(s => s.template);
    const hasTemplate = useCustomEmailTemplateStore(selectHasCustomTemplate) && isCustomEmailTemplateEnabled;
    const [language, setLanguage] = useState<TemplateLanguage | null>(null);
    const [loading, setLoading] = useState(false);
    const languageRef = useRef<TemplateLanguage | null>(null);
    const appliedRef = useRef<AppliedTemplate | null>(null);
    // `custom:${language}:${version}` → the draft as the server returned it, so switching back skips a request.
    const cacheRef = useRef(new Map<string, RelanceEmailDto>());

    const activate = useCallback((next: TemplateLanguage | null, applied: AppliedTemplate | null) => {
        languageRef.current = next;
        appliedRef.current = applied;
        setLanguage(next);
    }, []);

    // No version is recorded: a saved draft may come from an older template, so re-clicking the chip renders the current one.
    const adopt = useCallback((draft: RelanceEmailDto): RelanceEmailDto => {
        const draftLanguage = draft.customTemplateLanguage ?? null;
        activate(draftLanguage, null);
        return draftLanguage ? inDisplayForm(draft, draftLanguage) : draft;
    }, [activate]);

    const select = useCallback(async (requested?: TemplateLanguage): Promise<CustomSelection> => {
        const store = useCustomEmailTemplateStore.getState();
        if (!isCustomEmailTemplateEnabled || !selectHasCustomTemplate(store)) return { status: 'unavailable' };
        const version = store.version;
        const target = requested ?? languageRef.current ?? undefined;
        const applied = appliedRef.current;
        if (applied?.version === version && applied.language === (target ?? applied.language))
            return { status: 'unchanged' };

        const key = `custom:${target ?? 'auto'}:${version}`;
        let draft = cacheRef.current.get(key);
        if (!draft) {
            setLoading(true);
            try {
                draft = await emailsService.getCustomDraft(relanceId, target) ?? undefined;
            } finally {
                setLoading(false);
            }
            if (!draft) return { status: 'unavailable' };
            cacheRef.current.set(key, draft);
        }

        const draftLanguage = draft.customTemplateLanguage ?? target ?? 'FR';
        // The server picks the language when none is requested; picking it explicitly later must hit the cache.
        cacheRef.current.set(`custom:${draftLanguage}:${version}`, draft);
        const shown = inDisplayForm(draft, draftLanguage);
        activate(draftLanguage, { language: draftLanguage, version });
        applyDraft(shown);
        return { status: 'applied', draft: shown };
    }, [relanceId, applyDraft, activate]);

    /** {@link select} for a user action: a failure is reported rather than thrown. */
    const choose = async (requested?: TemplateLanguage) => {
        try {
            if ((await select(requested)).status === 'unavailable')
                showToast('error', t(TranslationKey.CustomerFollowUp.Email.CustomTemplate.LoadError));
        } catch (e) {
            console.error('Failed to load the custom email draft:', e);
            showToast('error', t(TranslationKey.CustomerFollowUp.Email.CustomTemplate.LoadError));
        }
    };

    const clear = useCallback(() => activate(null, null), [activate]);

    const ensureLoaded = useCallback(async () => {
        try {
            await useCustomEmailTemplateStore.getState().ensureLoaded();
        } catch (e) {
            console.error('Failed to load the custom email template; the custom tone is unavailable:', e);
        }
    }, []);

    const toSendable = (subject: string, body: string) => ({
        subject: toCanonical(subject),
        bodyTone: toCanonical(body),
        customTemplateLanguage: language,
    });

    return { language, languages: definedLanguages(template), hasTemplate, loading, adopt, select, choose, clear, ensureLoaded, toSendable };
};
